import React, { useState } from 'react';
import Modal from '../../Modal';
import { RSButton, RSInput, RSAlert } from 'reactsymbols-kit';
import Styles from '../../../styles/colors';
import type { IModelInformation } from '../../../stores/projects/model_information';
import { asRSChange, rsValue, type RSInputChange } from '../../Inspector/rsForm';

/** Props for the inner {@link SaveVersionModal} content. */
interface SaveVersionContentProps {
  /** The model to save a new version of. */
  model: IModelInformation;
  /** Close the modal. */
  onRequestClose: () => void;
}

/** Props for the {@link SaveVersionModalWrapper} (the default export). */
interface SaveVersionModalProps extends SaveVersionContentProps {
  /** Whether the modal is open. */
  isOpen: boolean;
}

/**
 * Save a new version of the model
 */
function SaveVersionModal({ model, onRequestClose }: SaveVersionModalProps) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  // check if the name is non-empty
  const valid = name && name.length > 0;

  // Handler for the save button: actually save the version and close the modal
  // if successful.
  const onSave = () => {
    setSaving(true);
    setError(false);
    model
      .saveVersion(name)
      .then(() => {
        onRequestClose();
      })
      .catch(() => {
        setSaving(false);
        setError(true);
      });
  };

  // onSubmit handler to make 'enter' work in the form
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (valid && !saving) {
      onSave();
    }
  };

  return (
    <div className="merlin-modal save-version-modal">
      <div className="title">Save a new model version</div>
      <form className="form" onSubmit={onSubmit}>
        <div className="modal-body">
          <div className="container">
            {error && (
              <div className="row">
                <div className="col">
                  <RSAlert level="danger" visible={true}>
                    <p>We couldn't save your new model version because of an unexpected error.</p>
                  </RSAlert>
                </div>
              </div>
            )}
            <div className="row">
              <div className="col">
                <RSInput
                  name="name"
                  label="Give your version a name"
                  value={name}
                  onChange={asRSChange<RSInputChange>((value: string) => setName(value))}
                  disabled={saving}
                  autoFocus={true}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="button-wrapper">
          <RSButton
            value="Cancel"
            type="button"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onRequestClose}
          />
          <RSButton
            type="submit"
            background={Styles.blueGradient}
            value={rsValue(saving ? null : 'Save')}
            iconName={rsValue(saving ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(saving ? 'spin' : null)}
            disabled={!valid || saving}
          />
        </div>
      </form>
    </div>
  );
}

// Wrap in Modal
const SaveVersionModalWrapper = ({ isOpen, onRequestClose, ...rest }: SaveVersionModalProps) => (
  <Modal isOpen={isOpen} onRequestClose={onRequestClose}>
    <SaveVersionModal isOpen={isOpen} onRequestClose={onRequestClose} {...rest} />
  </Modal>
);

export default SaveVersionModalWrapper;
